import Link from "next/link"
import { usePathname } from "next/navigation"
import React, { useEffect } from "react"
import { useSidebarContext } from "src/store/SidebarContext"
import { MENU_SECTIONS, MenuSection } from "./constants"
import styled from "styled-components"

export const SIDEBAR_WIDTH = 240

type Props = {
  isAdmin: boolean
  className?: string
}

const SidebarSection: React.FC<{
  section: MenuSection
  pathname: string
  isOpen: boolean
  onToggle: () => void
}> = ({ section, pathname, isOpen, onToggle }) => {
  return (
    <div className="sidebar-section">
      <button className="sidebar-section__header" onClick={onToggle}>
        <span className="sidebar-section__icon">{section.icon}</span>
        <span className="sidebar-section__title">{section.title}</span>
        <span className={`sidebar-section__arrow ${isOpen ? "open" : ""}`}>▼</span>
      </button>
      {isOpen && (
        <ul className="sidebar-section__items">
          {section.items.map((item) => {
            const isActive = pathname.startsWith(item.path)
            return (
              <li key={item.path}>
                <Link
                  href={item.path}
                  className={`sidebar-section__link ${isActive ? "active" : ""}`}
                  prefetch={false}
                >
                  {item.name}
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const Component: React.FC<Props> = ({ isAdmin, className }) => {
  const pathname = usePathname()
  const { openSections, setOpenSections, toggleSection } = useSidebarContext()

  useEffect(() => {
    if (!pathname) return

    const activeSection = MENU_SECTIONS.find((section) =>
      section.items.some((item) => pathname.startsWith(item.path))
    )

    if (activeSection) {
      setOpenSections((prev) => {
        if (prev[activeSection.title]) return prev

        return {
          ...prev,
          [activeSection.title]: true,
        }
      })
    }
  }, [pathname, setOpenSections])

  const visibleSections = MENU_SECTIONS.filter((section) => !section.adminOnly || isAdmin)

  return (
    <nav className={className}>
      {visibleSections.map((section) => (
        <SidebarSection
          key={section.title}
          section={section}
          pathname={pathname || ""}
          isOpen={openSections[section.title] ?? false}
          onToggle={() => toggleSection(section.title)}
        />
      ))}
    </nav>
  )
}

export const Sidebar = styled(Component)`
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  height: calc(100vh - 60px);
  background-color: #f8f9fa;
  border-right: 1px solid #e9ecef;
  overflow-y: auto;
  padding: 16px 0;

  .sidebar-section {
    margin-bottom: 4px;
  }

  .sidebar-section__header {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: #495057;
    text-align: left;
    transition: background-color 0.2s;

    &:hover {
      background-color: #e9ecef;
    }
  }

  .sidebar-section__icon {
    display: flex;
    align-items: center;
    margin-right: 10px;
    font-size: 18px;
    color: #6c757d;
  }

  .sidebar-section__title {
    flex: 1;
  }

  .sidebar-section__arrow {
    font-size: 10px;
    color: #6c757d;
    transition: transform 0.2s;

    &.open {
      transform: rotate(180deg);
    }
  }

  .sidebar-section__items {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .sidebar-section__link {
    display: block;
    padding: 8px 16px 8px 44px;
    font-size: 13px;
    color: #495057;
    text-decoration: none;
    transition: background-color 0.2s, color 0.2s;

    &:hover {
      background-color: #e9ecef;
    }

    &.active {
      background-color: #e7f1ff;
      color: #0d6efd;
      font-weight: 500;
    }
  }
`
